import type { AxiosInstance } from 'axios';

import { internalApiClient } from '@/marketing/infrastructure/http/httpClient';
import { pathSegment } from '@/marketing/lib/pathSegment';
import type {
  ContentTrackingExportPage,
  ContentTrackingExportPurpose,
  ContentTrackingListParams,
  ContentTrackingMetadata,
  ContentTrackingMetadataRequest,
  ContentTrackingPage,
  ContentTrackingRefreshRequest,
  ContentTrackingRefreshResult,
  ContentTrackingSparkRequest,
  ContentTrackingVideoRequest,
  ContentTrackingVideoResult,
} from '@/marketing/models/contentTracking';

const BASE_PATH = '/hub/campaigns';

// Every route lives on internal-api; the external facade has no content tracking surface.
export class ContentTrackingAPIClient {
  constructor(private readonly client: AxiosInstance = internalApiClient) {}

  public async listPage(campaignId: string, params: ContentTrackingListParams = {}): Promise<ContentTrackingPage> {
    const { data } = await this.client.get<ContentTrackingPage>(`${this.campaignPath(campaignId)}`, { params });
    return data;
  }

  public async getContentVersion(campaignId: string, influencerId: string): Promise<string> {
    const { data } = await this.client.get<{ contentVersion: string }>(
      `${BASE_PATH}/${pathSegment(campaignId)}/enrollments/${pathSegment(influencerId)}`,
    );
    return data.contentVersion;
  }

  public async exportPage(
    campaignId: string,
    purpose: ContentTrackingExportPurpose,
    params: ContentTrackingListParams = {},
  ): Promise<ContentTrackingExportPage> {
    const { data } = await this.client.get<ContentTrackingExportPage>(`${this.campaignPath(campaignId)}/export`, {
      params: { ...params, purpose },
    });
    return data;
  }

  public async updateMetadata(
    campaignId: string,
    influencerId: string,
    body: ContentTrackingMetadataRequest,
  ): Promise<ContentTrackingMetadata> {
    const { data } = await this.client.patch<ContentTrackingMetadata>(
      this.enrollmentPath(campaignId, influencerId),
      body,
    );
    return data;
  }

  public async updateSparkAdsCode(
    campaignId: string,
    influencerId: string,
    body: ContentTrackingSparkRequest,
  ): Promise<void> {
    await this.client.patch(`${this.enrollmentPath(campaignId, influencerId)}/sparkAdsCode`, body);
  }

  public async saveVideo(
    campaignId: string,
    influencerId: string,
    body: ContentTrackingVideoRequest,
  ): Promise<ContentTrackingVideoResult> {
    const { data } = await this.client.post<ContentTrackingVideoResult>(
      `${this.enrollmentPath(campaignId, influencerId)}/videos`,
      body,
    );
    return data;
  }

  public async refreshMetrics(
    campaignId: string,
    body: ContentTrackingRefreshRequest = {},
  ): Promise<ContentTrackingRefreshResult> {
    const { data } = await this.client.post<ContentTrackingRefreshResult>(
      `${this.campaignPath(campaignId)}/refreshMetrics`,
      body,
    );
    return data;
  }

  private campaignPath(campaignId: string): string {
    return `${BASE_PATH}/${pathSegment(campaignId)}/contentTracking`;
  }

  private enrollmentPath(campaignId: string, influencerId: string): string {
    return `${this.campaignPath(campaignId)}/${pathSegment(influencerId)}`;
  }
}

export const contentTrackingAPIClient = new ContentTrackingAPIClient();
